/**
 * Colonna centrale del client email: lista messaggi paginata della
 * cartella selezionata. Non letti in grassetto. Ricerca semplice.
 *
 * Multi-selezione: checkbox visibile su hover (sempre, se selezione
 * attiva), Ctrl/Cmd+click toggle, Shift+click intervallo. Con selezione
 * attiva la testata mostra la barra azioni bulk al posto della ricerca.
 */

import React, { useRef, useState } from 'react';
import {
  Archive,
  BellOff,
  Clock,
  FolderInput,
  Mail,
  MailOpen,
  Paperclip,
  Tag,
  Trash2,
  X,
} from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Tooltip,
} from '@floraltech/ui';
import type {
  BatchAzione,
  EmailCartella,
  EmailEtichetta,
  EmailMessaggioItem,
  MessaggiPage,
} from '../lib/api';
import { EtichettaChip } from './EtichettaChip';
import { EtichettePicker } from './EtichettePicker';
import { MenuContestuale, type VoceMenu } from './MenuContestuale';
import { MonitoraggioChip } from './MonitoraggioChip';

interface Props {
  pagina: MessaggiPage | null;
  loading: boolean;
  selezionatoId: string | null;
  query: string;
  selezione: Set<string>;
  destinazioni: EmailCartella[];
  onQuery: (q: string) => void;
  onSeleziona: (id: string) => void;
  onPagina: (page: number) => void;
  onSelezione: (s: Set<string>) => void;
  onBatch: (azione: BatchAzione, ids: string[], destId?: string, etichettaId?: string) => void;
  /** Casella PEC senza email.pec_gestione: niente multi-selezione/bulk/drag righe. */
  soloLettura?: boolean;
  /** Etichette note (id -> dato), per risolvere gli id in etichette dei messaggi. */
  etichette?: Map<string, EmailEtichetta>;
  /**
   * Avvia l'attesa di risposta su una mail inviata (voce del tasto destro).
   * Assente = menu contestuale disattivato (es. cartelle diverse da Inviata).
   */
  onMonitora?: (messaggio: EmailMessaggioItem) => void;
  /** Chiude un monitoraggio attivo dal tasto destro. */
  onNonMonitorare?: (messaggio: EmailMessaggioItem) => void;
}

export function MessaggiList({
  pagina,
  loading,
  selezionatoId,
  query,
  selezione,
  destinazioni,
  onQuery,
  onSeleziona,
  onPagina,
  onSelezione,
  onBatch,
  soloLettura = false,
  etichette = new Map(),
  onMonitora,
  onNonMonitorare,
}: Props) {
  const totPagine = pagina ? Math.max(1, Math.ceil(pagina.totale / pagina.page_size)) : 1;
  // Ancora per Shift+click (id ultima riga toccata, solo pagina corrente).
  const ultimoToccato = useRef<string | null>(null);
  // Menu contestuale aperto: posizione del cursore + riga su cui e' stato aperto.
  const [menu, setMenu] = useState<
    { x: number; y: number; messaggio: EmailMessaggioItem } | null
  >(null);

  const vociMenu = (m: EmailMessaggioItem): VoceMenu[] => {
    const voci: VoceMenu[] = [];
    const attivo = m.monitoraggio && ['in_attesa', 'scaduto'].includes(m.monitoraggio.stato);
    if (onMonitora && !attivo) {
      voci.push({
        etichetta: 'Monitora risposta...',
        icona: Clock,
        onClick: () => onMonitora(m),
      });
    }
    if (onNonMonitorare && attivo) {
      voci.push({
        etichetta: 'Non monitorare piu\'',
        icona: BellOff,
        onClick: () => onNonMonitorare(m),
      });
    }
    return voci;
  };

  const toggle = (id: string) => {
    const next = new Set(selezione);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    ultimoToccato.current = id;
    onSelezione(next);
  };

  const selezionaIntervallo = (id: string) => {
    const items = pagina?.items ?? [];
    const da = items.findIndex((m) => m.id === ultimoToccato.current);
    const a = items.findIndex((m) => m.id === id);
    if (da === -1 || a === -1) {
      toggle(id);
      return;
    }
    const [inizio, fine] = da < a ? [da, a] : [a, da];
    const next = new Set(selezione);
    for (let i = inizio; i <= fine; i++) next.add(items[i].id);
    ultimoToccato.current = id;
    onSelezione(next);
  };

  const clickRiga = (id: string, e: React.MouseEvent) => {
    if (soloLettura) {
      onSeleziona(id);
      return;
    }
    if (e.shiftKey) {
      e.preventDefault();
      selezionaIntervallo(id);
    } else if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      toggle(id);
    } else {
      onSeleziona(id);
    }
  };

  const idsPagina = pagina?.items.map((m) => m.id) ?? [];
  const tuttiSelezionati =
    idsPagina.length > 0 && idsPagina.every((id) => selezione.has(id));
  const ids = [...selezione];

  const toggleTutti = () => {
    if (tuttiSelezionati) onSelezione(new Set());
    else onSelezione(new Set(idsPagina));
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b p-2">
        {soloLettura || selezione.size === 0 ? (
          <Input
            placeholder="Cerca per oggetto o mittente..."
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onQuery(e.target.value)}
          />
        ) : (
          <div className="flex h-9 items-center gap-1">
            <input
              type="checkbox"
              className="h-4 w-4 accent-primary"
              checked={tuttiSelezionati}
              ref={(el) => {
                if (el) el.indeterminate = !tuttiSelezionati && selezione.size > 0;
              }}
              onChange={toggleTutti}
              aria-label="Seleziona tutti i messaggi della pagina"
            />
            <span className="mx-1 flex-1 truncate text-sm text-muted-foreground">
              {selezione.size === 1 ? '1 selezionato' : `${selezione.size} selezionati`}
            </span>
            <DropdownMenu>
              <Tooltip label="Sposta in cartella">
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" aria-label="Sposta in cartella">
                    <FolderInput className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
              </Tooltip>
              <DropdownMenuContent>
                {destinazioni.map((c) => (
                  <DropdownMenuItem key={c.id} onClick={() => onBatch('sposta', ids, c.id)}>
                    {c.nome_display ?? c.nome_imap}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Tooltip label="Segna come letti">
              <Button variant="ghost" size="sm" aria-label="Segna come letti"
                onClick={() => onBatch('letto', ids)}>
                <MailOpen className="h-4 w-4" />
              </Button>
            </Tooltip>
            <Tooltip label="Segna come da leggere">
              <Button variant="ghost" size="sm" aria-label="Segna come da leggere"
                onClick={() => onBatch('non_letto', ids)}>
                <Mail className="h-4 w-4" />
              </Button>
            </Tooltip>
            <Tooltip label="Archivia">
              <Button variant="ghost" size="sm" aria-label="Archivia selezionati"
                onClick={() => onBatch('archivia', ids)}>
                <Archive className="h-4 w-4" />
              </Button>
            </Tooltip>
            <Tooltip label="Elimina">
              <Button variant="ghost" size="sm" aria-label="Elimina selezionati"
                onClick={() => onBatch('elimina', ids)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </Tooltip>
            <EtichettePicker
              etichette={[...etichette.values()].filter((e) => e.attiva)}
              tooltip="Applica etichetta"
              onSeleziona={(e) => onBatch('applica_etichetta', ids, undefined, e.id)}
            />
            <EtichettePicker
              etichette={[...etichette.values()].filter((e) => e.attiva)}
              tooltip="Rimuovi etichetta"
              icon={
                <span className="relative inline-flex">
                  <Tag className="h-4 w-4" />
                  <X className="absolute -bottom-1 -right-1 h-2.5 w-2.5 rounded-full bg-background" />
                </span>
              }
              onSeleziona={(e) => onBatch('rimuovi_etichetta', ids, undefined, e.id)}
            />
            <Tooltip label="Annulla selezione">
              <Button variant="ghost" size="sm" aria-label="Annulla selezione"
                onClick={() => onSelezione(new Set())}>
                <X className="h-4 w-4" />
              </Button>
            </Tooltip>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <p className="p-4 text-sm text-muted-foreground">Caricamento...</p>
        )}
        {!loading && pagina && pagina.items.length === 0 && (
          <p className="p-4 text-sm text-muted-foreground">Nessun messaggio.</p>
        )}
        {!loading &&
          pagina?.items.map((m) => {
            const attivo = m.id === selezionatoId;
            const selezionata = selezione.has(m.id);
            const dataStr = m.data_ricezione
              ? new Date(m.data_ricezione).toLocaleDateString('it-IT', {
                  day: '2-digit',
                  month: 'short',
                })
              : '';
            const etichetteRisolte = (m.etichette ?? [])
              .map((id) => etichette.get(id))
              .filter((e): e is EmailEtichetta => e != null);
            const etichetteVisibili = etichetteRisolte.slice(0, 3);
            const etichetteInEccesso = etichetteRisolte.length - etichetteVisibili.length;
            return (
              <div
                key={m.id}
                className={`group flex w-full items-start border-b transition-colors ${
                  attivo ? 'bg-primary/10' : selezionata ? 'bg-primary/5' : 'hover:bg-muted'
                }`}
                onContextMenu={(e) => {
                  if (!onMonitora) return;
                  e.preventDefault();
                  setMenu({ x: e.clientX, y: e.clientY, messaggio: m });
                }}
                draggable={!soloLettura}
                onDragStart={(e) => {
                  if (soloLettura) return;
                  const drag = selezione.has(m.id) ? [...selezione] : [m.id];
                  e.dataTransfer.setData('application/x-email-ids', JSON.stringify(drag));
                  e.dataTransfer.effectAllowed = 'move';
                  // Ghost con conteggio (elemento temporaneo fuori schermo)
                  const ghost = document.createElement('div');
                  ghost.textContent =
                    drag.length === 1 ? '1 messaggio' : `${drag.length} messaggi`;
                  ghost.className =
                    'fixed -left-96 top-0 rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground';
                  document.body.appendChild(ghost);
                  e.dataTransfer.setDragImage(ghost, 10, 10);
                  requestAnimationFrame(() => document.body.removeChild(ghost));
                }}
              >
                {!soloLettura && (
                  <input
                    type="checkbox"
                    className={`ml-2 mt-3 h-4 w-4 shrink-0 accent-primary ${
                      selezione.size > 0 || selezionata
                        ? 'opacity-100'
                        : 'opacity-0 group-hover:opacity-100'
                    }`}
                    checked={selezionata}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggle(m.id)}
                    aria-label={`Seleziona ${m.oggetto || 'messaggio'}`}
                  />
                )}
                <button
                  onClick={(e) => clickRiga(m.id, e)}
                  className="block min-w-0 flex-1 px-2 py-2 text-left"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span
                      className={`truncate text-sm ${
                        m.flag_letto ? 'text-muted-foreground' : 'font-semibold'
                      }`}
                    >
                      {m.da_nome || m.da_email || '(sconosciuto)'}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">{dataStr}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span
                      className={`flex-1 truncate text-sm ${m.flag_letto ? '' : 'font-semibold'}`}
                    >
                      {m.oggetto || '(senza oggetto)'}
                    </span>
                    {m.ha_allegati && (
                      <Tooltip label="Ha allegati">
                        <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
                      </Tooltip>
                    )}
                  </div>
                  {m.anteprima && (
                    <p className="truncate text-xs text-muted-foreground">{m.anteprima}</p>
                  )}
                  {(etichetteVisibili.length > 0 || m.monitoraggio) && (
                    <div className="mt-0.5 flex items-center gap-1.5 overflow-hidden">
                      {m.monitoraggio && (
                        <MonitoraggioChip monitoraggio={m.monitoraggio} compatta />
                      )}
                      {etichetteVisibili.map((e) => (
                        <EtichettaChip key={e.id} etichetta={e} compatta />
                      ))}
                      {etichetteInEccesso > 0 && (
                        <span className="shrink-0 text-xs text-muted-foreground">
                          +{etichetteInEccesso}
                        </span>
                      )}
                    </div>
                  )}
                </button>
              </div>
            );
          })}
      </div>
      {pagina && pagina.totale > pagina.page_size && (
        <div className="flex items-center justify-between border-t p-2 text-sm">
          <Button
            variant="outline"
            size="sm"
            disabled={pagina.page <= 1}
            onClick={() => onPagina(pagina.page - 1)}
          >
            Precedente
          </Button>
          <span className="text-muted-foreground">
            {pagina.page} / {totPagine}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={pagina.page >= totPagine}
            onClick={() => onPagina(pagina.page + 1)}
          >
            Successiva
          </Button>
        </div>
      )}
      {menu && (
        <MenuContestuale
          posizione={{ x: menu.x, y: menu.y }}
          voci={vociMenu(menu.messaggio)}
          onChiudi={() => setMenu(null)}
        />
      )}
    </div>
  );
}
